import { Navigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

export default function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useUser();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const normalizedRequiredRoles = requiredRoles
    ?.map(role => normalizeRole(role))
    .filter((role): role is string => Boolean(role));

  useEffect(() => {
    const checkUserRole = async () => {
      // If no roles required, allow access for authenticated users
      if (!isAuthenticated || !user) {
        setIsChecking(false);
        setHasAccess(false);
        return;
      }

      // If no specific roles required, allow all authenticated users
      if (!normalizedRequiredRoles || normalizedRequiredRoles.length === 0) {
        setHasAccess(true);
        setIsChecking(false);
        return;
      }

      try {
        // Directly query the public.users table for the role
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking user role:', error);
          setHasAccess(false);
        } else if (data) {
          // Check if user's role is in the required roles
          const userRole = normalizeRole(data.role);
          const hasRole = userRole ? normalizedRequiredRoles.includes(userRole) : false;

          // Special case: admin and developer roles have access to everything
          const isAdmin = userRole === 'admin';
          const isDeveloper = userRole === 'developer';

          setHasAccess(hasRole || isAdmin || isDeveloper);
        } else {
          setHasAccess(false);
        }
      } catch (err) {
        console.error('Error in role check:', err);
        setHasAccess(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkUserRole();
  }, [isAuthenticated, user, normalizedRequiredRoles]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show loading state while checking permissions
  if (isChecking) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="w-8 h-8 mx-auto mb-4 border-4 border-t-transparent rounded-full animate-spin"></div>
        <p>Checking permissions...</p>
      </div>
    </div>;
  }

  // If roles are required, check if the user has permission
  if (normalizedRequiredRoles && normalizedRequiredRoles.length > 0) {
    // For client users, only allow access to private-network, bug-reports, and feature-requests
    const normalizedUserRole = normalizeRole(user?.role);

    if (normalizedUserRole === 'client' &&
        !normalizedRequiredRoles.includes('client')) {
      return <Navigate to="/private-network" replace />;
    }

    // For other users, check if they have the required roles
    if (hasAccess === false) {
      // Redirect to private network for users without required roles
      return <Navigate to="/private-network" replace />;
    }
  }

  return <>{children}</>;
}